import { useRef, useMemo, useState } from 'react';
import { layers } from 'app/spacing';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getCustomObjectsIsFetchingSelector,
  getCustomObjectsIsFilteringSelector,
} from 'store/customObjectsPage/customObjects.redux';
import Button from 'components/Button';
import { TRow } from 'components/Kizen/Table';
import BigTableLayout, {
  useBigTableLayout,
} from 'components/Layout/BigTableLayout';
import PageToolbar, {
  PageSearchInput,
  PageToolbarSection,
  PageToolbarTitle,
} from 'components/Layout/PageToolbar';
import TablePagination from 'components/Tables/Big/TablePagination';
import BigTable from 'components/Tables/Big';
import getCustomObjectsColumns, { CUSTOM_OBJECTS_ACTIONS } from './columns';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { CustomObjectHistoryModal } from './CustomObjectHistoryModal';

export default function CustomObjectTabularPage(props) {
  const {
    customObjects,
    customObjectsCount,
    search,
    onChangeSearch,
    sort,
    onChangeSort,
    pageNumber,
    pageSize,
    onChangePageNumber,
    onChangePageSize,
    onUpdateCustomObjectField,
    onSelectEditObjectSettings,
    onSelectEditCustomFields,
    onSelectDeleteCustomObject,
    openMenu,
    globalCustomObjectPermission,
    ...others
  } = props;

  const { t } = useTranslation();
  const isCustomObjectsFetching = useSelector(
    getCustomObjectsIsFetchingSelector
  );
  const isFiltering = useSelector(getCustomObjectsIsFilteringSelector);

  const { filterDropdownRef, scrollTransitioned, ...bigTablePageProps } =
    useBigTableLayout();

  const { scrolledToTable } = bigTablePageProps;

  // So that we can reference props in memoized values
  const propsRef = useRef();
  propsRef.current = props;

  const [historyObjectId, setHistoryObjectId] = useState(null);

  const columns = useMemo(
    () =>
      getCustomObjectsColumns({
        onUpdateField: (customObject, patch) =>
          propsRef.current.onUpdateCustomObjectField(customObject, patch),
        onSelectAction: ({ value }, customObject) => {
          const { id } = customObject;
          if (value === CUSTOM_OBJECTS_ACTIONS.EditObjectSettings) {
            propsRef.current.onSelectEditObjectSettings(customObject.id);
          }
          if (value === CUSTOM_OBJECTS_ACTIONS.EditCustomFields) {
            propsRef.current.onSelectEditCustomFields(id);
          }
          if (value === CUSTOM_OBJECTS_ACTIONS.Delete) {
            propsRef.current.onSelectDeleteCustomObject(customObject);
          }
          if (value === CUSTOM_OBJECTS_ACTIONS.History) {
            setHistoryObjectId(id);
          }
          if (value === CUSTOM_OBJECTS_ACTIONS.ViewApiDocs) {
            window.open(
              `${import.meta.env.VITE_API_BASE_PATH}/record-docs/${encodeURIComponent(customObject.name)}/swagger`,
              '_blank',
              'noopener,noreferrer'
            );
          }
        },
        t,
      }),
    [t]
  );

  const headData = useMemo(
    () => ({
      meta: {
        sort,
        onSort: (column, direction) => onChangeSort({ column, direction }),
      },
    }),
    [sort, onChangeSort]
  );

  return (
    <BigTableLayout
      filtersOpen={openMenu}
      toolbar={
        <PageToolbar>
          <PageToolbarSection>
            <PageSearchInput
              placeholder={t('Find Objects')}
              value={search}
              onChange={onChangeSearch}
              loading={isFiltering}
              isClearable
            />
          </PageToolbarSection>
          <PageToolbarTitle
            count={customObjectsCount}
            single={t('Object')}
            plural={t('Objects')}
            data-qa="objects-count"
          />
          <PageToolbarSection>
            {globalCustomObjectPermission.canCreate && (
              <>
                <Button
                  onClick={() =>
                    propsRef.current.onSelectEditObjectSettings('new')
                  }
                  {...getDataQAForInput('co-wizard-open-button')}
                >
                  {t('New Object')}
                </Button>
              </>
            )}
          </PageToolbarSection>
        </PageToolbar>
      }
      pagination={
        <TablePagination
          page={pageNumber}
          perPage={pageSize}
          totalCount={customObjectsCount}
          onChangePage={onChangePageNumber}
          onChangePerPage={onChangePageSize}
        />
      }
      recordTabSpacing={false}
      {...bigTablePageProps}
      {...others}
    >
      <BigTable
        scrolledToTable={scrolledToTable}
        columns={columns}
        staleData={isCustomObjectsFetching || isFiltering}
        head={<TRow head columns={columns} data={headData} />}
        zIndex={layers.content(0, 0)} // needs to at zero - {todo} reafctor big table so the z-indexes work with out hacks
      >
        {/* Just a spacer when there are no results */}
        {!customObjects.length && <tr />}
        {customObjects.map((customObject) => (
          <TRow key={customObject.id} columns={columns} data={customObject} />
        ))}
      </BigTable>
      {historyObjectId && (
        <CustomObjectHistoryModal
          id={historyObjectId}
          onHide={() => setHistoryObjectId(null)}
        />
      )}
    </BigTableLayout>
  );
}
